import React, { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  getAllProducts,
} from "../../../redux/Thunk/Product/ProductThunks";
import { fetchStores } from "../../../redux/Thunk/Store/StoreThunks";
import Pagination from "../../common/Pagination";
import {
  setSearchProducts,
  setSearchStore,
  setSearchStoreProducts,
} from "../../../redux/Reducer/Product/ProductSlice";
import { setStoreById } from "../../../redux/Reducer/Store/StoreSlice";
import {
  setActiveForm,
  setActiveTab,
} from "../../../redux/Reducer/Admin/PartnerSlice";
import { searchProduct as searchProducts } from "../../../redux/Thunk/Product/ProductThunks";
import AdminTable from "../../common/Table";

import Loader from "../../Loader";
import { Tooltip } from "../../common/Tooltip";
import SearchSelectStore from "./SearchSelectedStore";
import ProductTagsSelector from "./ProductTagsSelector";

function Stock() {
  const {
    currentPage: page,
    products,
    totalItems,
    totalPages,
    searchProduct,
    searchStore,
    loading,
  } = useSelector((state) => state?.product);
  const role = useSelector((state) => state?.auth?.role);

  const stores = useSelector((state) => state?.store?.stores || []);
  const [productList, setProductList] = useState([]);
  const [selectedFilterTags, setSelectedFilterTags] = useState([]);
  const [selectedStoreIds, setSelectedStoreIds] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [limit] = useState(10);
  const dispatch = useDispatch();

  useEffect(() => {
    const debounceTimeout = setTimeout(() => {
      dispatch(fetchStores());
    }, 100);

    return () => clearTimeout(debounceTimeout);
  }, [dispatch]);

  useEffect(() => {
    dispatch(setSearchStore(""));
    dispatch(setSearchProducts([]));
    dispatch(setStoreById({}));
  }, [dispatch]);

  const tagIdsString = useMemo(() => {
    return selectedFilterTags
      .map((t) => (typeof t === "object" ? t.id : t))
      .filter(Boolean)
      .join(",");
  }, [selectedFilterTags]);

  useEffect(() => {
    const fetchProducts = async () => {
      try {
        const current_page = currentPage;
        const response = await dispatch(
          getAllProducts({
            current_page,
            SID: searchStore || "",
            PID: "",
            tagIds: tagIdsString,
          })
        ).unwrap();

        if (response?.status === 200) {
          dispatch(setSearchStoreProducts(response?.data?.products));
        }
      } catch (error) {
        console.error("Failed to fetch products:", error);
      }
    };

    fetchProducts();
  }, [currentPage, searchStore, tagIdsString, dispatch]);

  useEffect(() => {
    let sourceProducts = searchProduct?.length > 0 ? searchProduct : products;
    let filteredProducts = Array.isArray(sourceProducts) ? [...sourceProducts] : [];

    if (selectedFilterTags?.length > 0) {
      filteredProducts = filteredProducts.filter((product) => {
        const productTags = product?.tags || product?.product?.tags || [];
        if (!productTags || productTags.length === 0) return false;
        return selectedFilterTags.some((sTag) => {
          const sId = typeof sTag === "object" ? sTag.id : sTag;
          const sName = (typeof sTag === "object" ? sTag.name : String(sTag))?.toUpperCase();
          return productTags.some((pTag) => {
            const pId = typeof pTag === "object" ? pTag.id : pTag;
            const pName = (typeof pTag === "object" ? pTag.name : String(pTag))?.toUpperCase();
            return (
              (sId !== undefined && sId !== null && pId !== undefined && pId !== null && String(sId) === String(pId)) ||
              (sName && pName && sName === pName)
            );
          });
        });
      });
    }

    setProductList(filteredProducts);
  }, [products, searchStore, searchProduct, selectedFilterTags]);

  useEffect(() => {
    setCurrentPage(1);
  }, [selectedFilterTags, searchStore]);

  const displayedProducts = productList;

  const handleNextPage = () => {
    if (currentPage < totalPages) {
      const nextPageNum = currentPage + 1;
      setCurrentPage(nextPageNum);
      dispatch(
        getAllProducts({
          current_page: nextPageNum,
          SID: searchStore || "",
          PID: "",
          tagIds: tagIdsString,
        })
      );
    }
  };

  const handlePreviousPage = () => {
    if (currentPage > 1) {
      const prevPageNum = currentPage - 1;
      setCurrentPage(prevPageNum);
      dispatch(
        getAllProducts({
          current_page: prevPageNum,
          SID: searchStore || "",
          PID: "",
          tagIds: tagIdsString,
        })
      );
    }
  };

  const handleView = React.useCallback(async (row) => {
    let name = row?.product?.productName || row?.productName || "";
    let IID = row?.IID || row?.product?.IID || "";

    dispatch(setActiveForm("editProduct"));
    dispatch(setActiveTab({ name: "Products", index: 0 }));
    await dispatch(searchProducts({ name, IID }));
  }, [dispatch]);

  const columns = useMemo(
    () => [
      {
        Header: "Sr.",
        accessor: (row, index) => (currentPage - 1) * limit + index + 1,
        width: "60px",
        Cell: ({ value }) => (
          <span className="font-semibold text-gray-500 text-[12px]">{value}</span>
        ),
      },
      {
        Header: "Product Name & ID",
        accessor: (row) => row,
        Cell: ({ value: row }) => (
          <div className="flex flex-col min-w-0 py-0.5">
            <button
              type="button"
              onClick={() => handleView(row)}
              className="text-left font-bold text-[13px] text-secondry-900 hover:text-primary-600 hover:underline leading-snug cursor-pointer group inline-flex items-center gap-1.5 transition-colors"
              title="Click to view product details in Products tab"
            >
              <span className="truncate">{row?.productName || "Unnamed Product"}</span>
              <svg
                className="w-3.5 h-3.5 text-gray-400 group-hover:text-primary-600 opacity-0 group-hover:opacity-100 transition-all transform group-hover:translate-x-0.5 shrink-0"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                strokeWidth="2.5"
              >
                <path strokeLinecap="round" strokeLinejoin="round" d="M13.5 6H5.25A2.25 2.25 0 003 8.25v10.5A2.25 2.25 0 005.25 21h10.5A2.25 2.25 0 0018 18.75V10.5m-10.5 6L21 3m0 0h-5.25M21 3v5.25" />
              </svg>
            </button>
            <div className="flex items-center gap-1.5 mt-1 flex-wrap">
              <span className="px-1.5 py-0.2 bg-gray-100 text-gray-700 font-mono text-[10px] font-bold rounded border border-gray-200">
                #{row?.IID}
              </span>
              {row?.type && (
                <span className="px-1.5 py-0.2 bg-blue-50 text-blue-700 text-[10px] font-semibold rounded border border-blue-100">
                  {row?.type}
                </span>
              )}
              <span className="px-1.5 py-0.2 bg-purple-50 text-purple-700 text-[10px] font-semibold rounded border border-purple-200">
                {row?.lookupMargin?.type || "Default"}
              </span>
            </div>
          </div>
        ),
      },
      {
        Header: "Admin Stock",
        accessor: (row) => row?.productStock ?? 0,
        width: "130px",
        Cell: ({ value, row }) => {
          const qty = Number(value);
          const uom = row?.uom ? ` (${row.uom})` : "";
          let badgeStyle = "bg-emerald-50 text-emerald-700 border-emerald-200";
          let dotColor = "bg-emerald-500";

          if (qty === 0) {
            badgeStyle = "bg-rose-50 text-rose-700 border-rose-200";
            dotColor = "bg-rose-500";
          } else if (qty <= 10) {
            badgeStyle = "bg-amber-50 text-amber-700 border-amber-200";
            dotColor = "bg-amber-500";
          }

          return (
            <span
              className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-bold border ${badgeStyle}`}
            >
              <span className={`w-1.5 h-1.5 rounded-full ${dotColor}`}></span>
              {qty > 0 ? `${qty}${uom}` : "Out of Stock"}
            </span>
          );
        },
      },
      {
        Header: "Pricing",
        accessor: (row) => row,
        width: "130px",
        Cell: ({ value: row }) => {
          const mrp = row?.pricing?.mrp;
          const gst = row?.pricing?.gst;
          const discount = row?.lookupMargin?.discount ?? row?.discount;
          return (
            <div className="flex flex-col min-w-0">
              <span className="font-bold text-[13px] text-secondry-900 leading-tight">
                {mrp ? `₹${mrp}` : "-"}
              </span>
              <div className="flex items-center gap-1.5 mt-1 flex-wrap">
                {gst ? (
                  <span className="text-[10px] text-gray-500 font-medium">
                    GST: {gst}%
                  </span>
                ) : null}
                {discount !== undefined && discount !== null ? (
                  <span className="text-[10px] font-semibold text-emerald-700 bg-emerald-50 px-1.5 py-0.2 rounded border border-emerald-100">
                    {discount}% Off
                  </span>
                ) : null}
              </div>
            </div>
          );
        },
      },
      {
        Header: "Store Stock",
        accessor: (row) => row?.storeProducts,
        width: "180px",
        Cell: ({ value }) => {
          if (!value || !Array.isArray(value) || value.length === 0) {
            return <span className="text-gray-400 text-[11px] italic">No stores</span>;
          }
          return (
            <div className="flex flex-col gap-1.5">
              {value.map((store, index) => {
                const storeObj = stores?.find((s) => String(s.SID) === String(store?.SID));
                const storeName = storeObj?.title || storeObj?.storeName || store?.storeName || `Store ${store?.SID}`;
                const qty = store?.storeStock || 0;
                return (
                  <div
                    key={index}
                    className="flex flex-col p-1.5 bg-sky-50/70 border border-sky-100 rounded-[6px]"
                  >
                    <div className="flex items-center justify-between gap-1">
                      <span className="font-bold text-[11px] text-sky-900 truncate max-w-[120px]" title={storeName}>
                        {storeName}
                      </span>
                      <span className="px-1.5 py-0.2 bg-sky-200/80 text-sky-900 font-mono text-[10px] font-extrabold rounded shrink-0">
                        {qty}
                      </span>
                    </div>
                    <span className="text-[10px] font-mono text-sky-600 font-semibold mt-0.5">
                      #SID: {store?.SID}
                    </span>
                  </div>
                );
              })}
            </div>
          );
        },
      },
      {
        Header: "Tags",
        accessor: (row) => row?.tags,
        width: "150px",
        Cell: ({ value }) => {
          if (!value || !Array.isArray(value) || value.length === 0) {
            return <span className="text-gray-400 text-[11px] italic">None</span>;
          }
          return (
            <div className="flex flex-wrap gap-1 items-center">
              {value.map((t, idx) => {
                const name = typeof t === "object" ? t?.name : String(t);
                const color = typeof t === "object" ? t?.color : "#3B82F6";
                return (
                  <span
                    key={idx}
                    className="px-2 py-0.5 rounded-full text-[10px] font-semibold text-white inline-block shadow-2xs whitespace-nowrap"
                    style={{ backgroundColor: color || "#3B82F6" }}
                  >
                    {name}
                  </span>
                );
              })}
            </div>
          );
        },
      },
      {
        Header: "Manufacturer",
        accessor: (row) => row?.manufacturer,
        width: "140px",
        Cell: ({ value }) => (
          <Tooltip>
            <span className="text-[12px] text-gray-700 font-medium truncate block max-w-[130px]">
              {value || "-"}
            </span>
            <span className="tooltip-text">{value}</span>
          </Tooltip>
        ),
      },
    ],
    [currentPage, limit, handleView, stores]
  );

  const handleResetAllFilters = () => {
    setSelectedFilterTags([]);
    setSelectedStoreIds([]);
    dispatch(setSearchStore(""));
  };

  const isFilterActive = selectedFilterTags.length > 0 || selectedStoreIds.length > 0;

  return (
    <section className="bg-white border border-primary-100 rounded-[12px] shadow-xs overflow-hidden">
      <Loader loading={loading} />

      {/* Top Header & Filter Toolbar */}
      <div className="p-4 bg-gray-50/70 border-b border-primary-100 flex flex-col lg:flex-row lg:items-center justify-between gap-4">
        {/* Metric Badges */}
        <div className="flex items-center gap-3 flex-wrap">
          <div className="flex items-center gap-2 px-3 py-1.5 bg-white border border-primary-200 rounded-[8px] shadow-2xs">
            <span className="text-[11px] font-bold uppercase text-primary-500 tracking-wider">
              Total Stock Items:
            </span>
            <span className="text-[13px] font-black text-primary-900 font-mono">
              {totalItems || displayedProducts?.length || 0}
            </span>
          </div>
          {isFilterActive && (
            <div className="flex items-center gap-2 px-3 py-1.5 bg-emerald-50 border border-emerald-200 rounded-[8px]">
              <span className="text-[11px] font-bold uppercase text-emerald-700 tracking-wider">
                Matching Results:
              </span>
              <span className="text-[13px] font-black text-emerald-900 font-mono">
                {displayedProducts?.length || 0}
              </span>
            </div>
          )}
        </div>

        {/* Filter Toolbar Controls */}
        <div className="flex items-center gap-3 flex-wrap lg:flex-nowrap min-w-0">
          {role !== "store" && (
            <div className="w-full sm:w-[260px] lg:w-[300px]">
              <SearchSelectStore
                type="stock"
                selectedStoreIds={selectedStoreIds}
                onStoreChange={(sids) => setSelectedStoreIds(sids)}
                placeholder="Filter by stores..."
              />
            </div>
          )}
          <div className="w-full sm:w-[280px] lg:w-[340px]">
            <ProductTagsSelector
              selectedTags={selectedFilterTags}
              onTagsChange={(tags) => setSelectedFilterTags(tags)}
              label={null}
              placeholder="Filter by tags..."
            />
          </div>
          {isFilterActive && (
            <button
              type="button"
              onClick={handleResetAllFilters}
              className="text-[12px] font-bold text-rose-600 hover:text-rose-800 underline whitespace-nowrap px-1"
            >
              Reset Filters
            </button>
          )}
        </div>
      </div>

      {/* Main Table or Clean Empty State */}
      {displayedProducts && displayedProducts.length > 0 ? (
        <AdminTable columns={columns} filteredData={displayedProducts} />
      ) : (
        <div className="p-12 text-center flex flex-col items-center justify-center bg-white">
          <div className="w-12 h-12 rounded-full bg-primary-50 flex items-center justify-center text-primary-500 mb-3 border border-primary-100">
            <svg
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <circle cx="11" cy="11" r="8"></circle>
              <line x1="21" y1="21" x2="16.65" y2="16.65"></line>
              <line x1="8" y1="11" x2="14" y2="11"></line>
            </svg>
          </div>
          <h3 className="text-[15px] font-bold text-secondry-800 mb-1">
            No stock products found
          </h3>
          <p className="text-[13px] text-gray-500 max-w-sm mb-4">
            {isFilterActive
              ? "No products match the selected store or tag filters."
              : "No inventory products are currently available."}
          </p>
          {isFilterActive && (
            <button
              type="button"
              onClick={handleResetAllFilters}
              className="px-4 py-1.5 bg-primary-600 hover:bg-primary-700 text-white text-[12px] font-semibold rounded-full transition-all shadow-xs"
            >
              Clear All Filters
            </button>
          )}
        </div>
      )}

      {/* Pagination Component */}
      <Pagination
        currentPage={page}
        totalPages={totalPages}
        onPreviousPage={handlePreviousPage}
        onNextPage={handleNextPage}
        isPreviousDisabled={currentPage === 1}
        isNextDisabled={currentPage === totalPages}
        totalItems={totalItems}
        itemLength={displayedProducts?.length}
        limit={limit}
      />
    </section>
  );
}

export default Stock;
